/**
 * Org roles → permissions (docs/03 "Org roles → permissions", ADR-0011).
 *
 *   owner ⊇ admin ⊇ {operator, finance} ⊇ viewer
 *
 * Edited only by WP00 and by a WP that introduces a new permission (one line per permission,
 * reviewed). The table in docs/03 is reproduced verbatim in test/roles.spec.ts, so the two
 * cannot drift apart silently.
 */
import { isReadPermission, PERMISSIONS, type Permission } from './permissions/index.js';

export const ORG_ROLES = ['owner', 'admin', 'operator', 'finance', 'viewer'] as const;
export type OrgRole = (typeof ORG_ROLES)[number];

export function isOrgRole(value: unknown): value is OrgRole {
  return typeof value === 'string' && (ORG_ROLES as readonly string[]).includes(value);
}

/** viewer: all `*:read` (billing:read is finance and above, docs/05), directory:browse, message:read. */
const VIEWER: Permission[] = [
  ...PERMISSIONS.filter((p) => isReadPermission(p) && p !== 'billing:read'),
  'directory:browse',
  'message:read',
];

const OPERATOR_ADDS: Permission[] = [
  'message:send',
  'profile:edit',
  'review:write',
  'connection:respond',
  'catalogue:manage',
  'rate:manage',
  'rate:share',
  'quote:manage',
  'shipment:write',
  'shipment:transition',
  'shipment:collaborate',
  'charge:write',
  'charge:confirm',
  'bill:issue',
  'bill:confirm',
  'payment_request:create',
  'payment:log',
  'hs:classify',
  'duty:estimate',
  'tracking:manage',
  'document:render',
  'dispute:manage',
  'claim:file',
  'event:register',
  'rfq:post',
  'rfq:respond',
  'rfq:award',
  'offer:post',
  'offer:respond',
];

const FINANCE_ADDS: Permission[] = [
  'shipment:finance_lock',
  'charge:confirm',
  'charge:adjust',
  'statement:issue',
  'invoice:issue',
  'invoice:void',
  'payment_request:approve',
  'writeoff:post',
  'cash:record',
  'payment:log',
  'payment:confirm',
  'org:periods:lock',
  'billing:read',
  'document:render',
];

const ADMIN_ADDS: Permission[] = [
  'members:manage',
  'org:manage',
  'org:links:manage',
  'connection:initiate',
  'contact:reveal',
  'verification:manage',
  'rate:approve',
  'shipment:withdraw',
  'rail:manage',
  'document:sign',
];

const OWNER_ADDS: Permission[] = [
  'billing:manage',
  'members:grant',
  'rate:view_cost',
  'shipment:release_override',
];

const viewer = new Set(VIEWER);
const operator = new Set([...viewer, ...OPERATOR_ADDS]);
const finance = new Set([...viewer, ...FINANCE_ADDS]);
const admin = new Set([...operator, ...finance, ...ADMIN_ADDS]);
const owner = new Set([...admin, ...OWNER_ADDS]);

export const ROLE_PERMISSIONS: Readonly<Record<OrgRole, ReadonlySet<Permission>>> = {
  owner,
  admin,
  operator,
  finance,
  viewer,
};

/**
 * Permissions that no role below owner receives from its role map; they reach a membership
 * only through an explicit `identity.permission_grants` row with a reason.
 */
export const GRANT_ONLY_PERMISSIONS: readonly Permission[] = ['rate:view_cost', 'shipment:release_override'];

/** Money-moving permissions denied while impersonating (docs/03 "Impersonation"). */
export const IMPERSONATION_DENIED_PERMISSIONS: readonly Permission[] = [
  'writeoff:post',
  'invoice:issue',
  'payment_request:approve',
  'billing:manage',
];

/** The actor's effective set: role map ∪ grants (docs/03 "The actor"). */
export function permissionsFor(role: OrgRole, grants: Iterable<Permission> = []): ReadonlySet<Permission> {
  return new Set([...ROLE_PERMISSIONS[role], ...grants]);
}
